import differenceWith from "lodash-es/differenceWith";
import { useCallback, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";

import FlashContext from "components/FlashMessages/FlashContext";
import ReadMoreDocsLink from "components/ReadMoreDocsLink";
import { useObserveForWarning } from "components/WarningContext/useObserveForWarning";
import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import ComboBox from "ds/components/ComboBox";
import ComboBoxDefaultItem from "ds/components/ComboBox/DefaultItem";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import TooltipModalBody from "ds/components/TooltipModal/Body";
import { TooltipModalTitle } from "ds/components/TooltipModal/Title";
import useTypedContext from "hooks/useTypedContext";
import { getDocsUrl } from "utils/getDocsUrl";
import Card from "ds/components/Card";

import { useNewStackAnalyticsSegmentEvent } from "../../useNewStackAnalyticsSegmentEvent";
import { AttachContextFormFields, AttachContextFormProps } from "./types";

const priorityFieldOptions = {
  valueAsNumber: true,
  min: 0,
  required: true,
  validate: (value: number) => /^[0-9]+$/.test(value.toString()),
};

const AttachContextForm = ({
  spaceContexts,
  attachedContextIds,
  onAttachContext,
  children,
}: AttachContextFormProps) => {
  const { onError, reportSuccess } = useTypedContext(FlashContext);

  const trackSegmentEvent = useNewStackAnalyticsSegmentEvent();

  const form = useForm<AttachContextFormFields>({
    defaultValues: {
      contextId: "",
      priority: 0,
    },
    mode: "onChange",
  });

  const { control, register, handleSubmit, formState, reset, watch } = form;

  const contextId = watch("contextId");

  useObserveForWarning(
    !!contextId,
    <>
      You have not attached the <strong>selected context</strong>. Do you want to continue without
      attaching it?
    </>
  );

  const contexts = useMemo(
    () =>
      differenceWith(
        spaceContexts.map((context) => ({
          value: context.id,
          label: context.name,
        })),
        attachedContextIds,
        (a, b) => a.value === b
      ),
    [spaceContexts, attachedContextIds]
  );

  const onSave = useCallback(
    () =>
      handleSubmit(async (formData: AttachContextFormFields) => {
        try {
          const contextAttachId = await onAttachContext(formData.contextId, formData.priority);

          if (contextAttachId) {
            reportSuccess({ message: "Context successfully attached" });

            reset();

            trackSegmentEvent("Context attached");
          }
        } catch (error) {
          onError(error);
        }
      })(),
    [onAttachContext, reportSuccess, reset, onError, trackSegmentEvent, handleSubmit]
  );

  return (
    <>
      <Banner variant="info">
        You can only attach contexts from the current space and parent spaces that you inherit from.
      </Banner>
      <Card variant="secondary" contentGap="medium">
        <Box gap="medium" grid gridTemplate="1fr 6.5rem" align="end">
          <Controller
            name="contextId"
            control={control}
            rules={{ required: "Context is required." }}
            render={({ field, fieldState }) => (
              <ComboBox
                label="Select context"
                error={fieldState.error?.message}
                items={contexts}
                value={field.value}
                onChange={field.onChange}
                tooltipInfo={
                  <>
                    <TooltipModalTitle>Context</TooltipModalTitle>
                    <TooltipModalBody align="start">
                      Context is a bundle of configuration elements (environment variables and
                      mounted files) independent of any stack that can be managed separately and
                      attached to as many or as few stacks as necessary.
                      <ReadMoreDocsLink docsUrl={getDocsUrl("/concepts/configuration/context")} />
                    </TooltipModalBody>
                  </>
                }
              >
                {(item) => <ComboBoxDefaultItem id={item.value} label={item.label} />}
              </ComboBox>
            )}
          />

          <FormField
            noMargin
            label="Priority"
            tooltipInfoVariant="modal"
            tooltipInfo={
              <>
                <TooltipModalTitle>Priority</TooltipModalTitle>
                <TooltipModalBody align="start">
                  By default contexts attached to stack will be executed from lowest (0) value. You
                  can change the ordering by hovering over Priority tag and clicking “edit priority”
                  from the menu. Edited values don't need to be unique.
                  <br />
                  <br />
                  Ordering can be also changed later from contexts tab within stack view. Please
                  note that auto-attached contexts prioritisation can not be changed and is based on
                  alphabetical order.
                  <ReadMoreDocsLink
                    docsUrl={getDocsUrl("/concepts/configuration/context#on-priority")}
                  />
                </TooltipModalBody>
              </>
            }
          >
            {({ ariaInputProps }) => (
              <Input
                error={!!formState.errors.priority}
                aria-label="Priority"
                {...register("priority", priorityFieldOptions)}
                {...ariaInputProps}
              />
            )}
          </FormField>
        </Box>
        <Box justify="end" margin="medium 0 0 0">
          <Button
            variant="contrast"
            type="submit"
            onPress={onSave}
            loading={formState.isSubmitting}
            disabled={!formState.isValid || formState.isSubmitting}
          >
            Attach
          </Button>
        </Box>

        {children}
      </Card>
    </>
  );
};

export default AttachContextForm;
